import { Dispatch, FC, SetStateAction } from "react";
import { Form, Formik } from "formik";
import { add, parseISO } from "date-fns";

import {
    Button,
    Select,
    Input,
    Checkbox,
    MaskedInput,
    AlertError,
    DatePicker,
} from "../../ui/_index";

import useLogic from "./DataForm.logic";

import { SelectOption, SelectOptionGroup } from "../../ui/Select";
import { NewCarBookingSuccessResponse, RentalPrice } from "../../../api/types";
import { FormatMoney } from "../../../utils/format";

interface Props {
    places: SelectOption[] | SelectOptionGroup[];
    pickupPlace: SelectOption;
    offer: RentalPrice;
    setBooking: Dispatch<
        SetStateAction<NewCarBookingSuccessResponse | undefined>
    >;
    setOffer: Dispatch<SetStateAction<RentalPrice | undefined>>;
}

const DataForm: FC<Props> = ({
    places,
    pickupPlace,
    offer,
    setBooking,
    setOffer,
}) => {
    const {
        initialValues,
        validationSchema,
        onSubmit,
        stateslist,
        pickupTimes,
        hasError,
        setHasError,
        errorMessage,
    } = useLogic(pickupPlace);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => onSubmit(values, offer, pickupPlace, setBooking)}
        >
            {({ errors, isSubmitting, setFieldValue, values, touched }) => {
                return (
                    <Form className="tw-pt-4 pb-6 sm:tw-pb-12 md:tw-pb-6 tw-px-4 sm:tw-px-6 lg:tw-px-6 lg:tw-row-start-1 lg:tw-col-start-1">
                        <div className="tw-max-w-lg tw-mx-auto lg:tw-max-w-none">
                            <section aria-labelledby="contact-info-heading">
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <h2
                                        id="contact-info-heading"
                                        className="tw-text-lg tw-font-medium tw-text-gray-900"
                                    >
                                        Información de Contacto
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={() => setOffer(undefined)}
                                        className="tw-text-sm tw-font-medium tw-text-orange-600 hover:tw-text-orange-800"
                                    >
                                        Cambiar renta
                                    </button>
                                </div>

                                <div className="tw-grid tw-grid-cols-12 tw-gap-y-6 tw-gap-x-4 tw-mt-6 ">
                                    <div className="tw-col-span-12 sm:tw-col-span-7">
                                        <Input
                                            label="Nombre completo"
                                            name="fullName"
                                            value={values.fullName}
                                            helpText="Según aparece en su licencia de conducción."
                                            onChange={(e) => setFieldValue("fullName", e)}
                                            error={errors.fullName}
                                            touched={touched.fullName}
                                        />
                                    </div>
                                    <div className="tw-col-span-12 sm:tw-col-span-5">
                                        <DatePicker
                                            label={"Fecha de nacimiento"}
                                            name="birthDate"
                                            value={values.birthDate}
                                            pages={1}
                                            maxDate={add(new Date(), { years: -21 })}
                                            minDate={add(new Date(), { years: -80 })}
                                            onChange={(e: any) => {
                                                setFieldValue("birthDate", e.value);
                                            }}
                                            error={errors.birthDate}
                                            touched={touched.birthDate}
                                            helpText="Edad mínima es 21 años."
                                        />
                                    </div>
                                    <div className="tw-col-span-12">
                                        <Input
                                            label="Correo electrónico"
                                            name="email"
                                            value={values.email}
                                            onChange={(e) => setFieldValue("email", e)}
                                            error={errors.email}
                                            touched={touched.email}
                                        />
                                    </div>
                                    <div className="tw-col-span-8">
                                        <MaskedInput
                                            mask="+1 999-999-9999"
                                            type="tel"
                                            label="Teléfono"
                                            onChange={(e) => setFieldValue("phone", e)}
                                        />
                                    </div>
                                    <div className="tw-col-span-4">
                                        <Input
                                            label="Código Postal"
                                            name="postalCode"
                                            value={values.postalCode}
                                            onChange={(e) => setFieldValue("postalCode", e)}
                                            error={errors.postalCode}
                                            touched={touched.postalCode}
                                        />
                                    </div>
                                </div>
                            </section>

                            <section aria-labelledby="shipping-heading" className="tw-mt-6">
                                <hr />
                                <div className="tw-mt-6 tw-grid tw-grid-cols-2 tw-gap-y-6 tw-gap-x-4 sm:tw-grid-cols-12 tw-mb-6">
                                    <div className="sm:tw-col-span-6">
                                        <Input
                                            label="Número de Licencia"
                                            name="licenseNumber"
                                            value={values.licenseNumber}
                                            onChange={(e) => setFieldValue("licenseNumber", e)}
                                            error={errors.licenseNumber}
                                            touched={touched.licenseNumber}
                                        />
                                    </div>

                                    <div className="sm:tw-col-span-6">
                                        <Select
                                            label="Estado de Licencia"
                                            items={stateslist}
                                            value={values.licenseState}
                                            onChange={(e) => setFieldValue("licenseState", e)}
                                            error={errors.licenseState?.value}
                                            touched={touched.licenseState?.value}
                                        />
                                    </div>
                                    <div className="sm:tw-col-span-6">
                                        <DatePicker
                                            label={"Expedición de Licencia"}
                                            name="licenseIssuedDate"
                                            value={values.licenseIssuedDate}
                                            pages={1}
                                            maxDate={add(new Date(), { days: -1 })}
                                            minDate={add(new Date(), { years: -10 })}
                                            onChange={(e: any) => {
                                                setFieldValue("licenseIssuedDate", e.value);
                                            }}
                                            error={errors.licenseIssuedDate}
                                            touched={touched.licenseIssuedDate}
                                        />
                                    </div>
                                    <div className="sm:tw-col-span-6">
                                        <DatePicker
                                            label={"Vencimiento de Licencia"}
                                            name="licenseExpirationDate"
                                            pages={1}
                                            minDate={add(parseISO(offer.end), { days: 1 })}
                                            onChange={(e: any) => {
                                                setFieldValue("licenseExpirationDate", e.value);
                                            }}
                                            value={values.licenseExpirationDate}
                                            error={errors.licenseExpirationDate}
                                            touched={touched.licenseExpirationDate}
                                        />
                                    </div>
                                </div>
                                <hr />
                                <div className="tw-mt-6 tw-grid tw-grid-cols-2 tw-gap-y-6 tw-gap-x-4 sm:tw-grid-cols-12 ">
                                    <div className="tw-col-span-2 sm:tw-col-span-8">
                                        <Input
                                            label="Lugar de recogida"
                                            value={pickupPlace.name}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className="tw-col-span-2 sm:tw-col-span-4">
                                        <Select
                                            label="Hora de Recogida"
                                            items={pickupTimes}
                                            value={values.pickupTime}
                                            onChange={(e) => setFieldValue("pickupTime", e)}
                                            error={errors.pickupTime?.value}
                                            touched={touched.pickupTime?.value}
                                        />
                                    </div>

                                    <div className="tw-col-span-2 sm:tw-col-span-12">
                                        <Select
                                            label="Lugar de Entrega"
                                            items={places}
                                            value={values.dropoffPlace}
                                            onChange={(e) => setFieldValue("dropoffPlace", e)}
                                            error={errors.dropoffPlace?.value}
                                            touched={touched.dropoffPlace?.value}
                                        />
                                    </div>
                                </div>
                            </section>

                            {hasError && errorMessage && (
                                <AlertError
                                    message={errorMessage}
                                    setShow={(value) => setHasError(value)}
                                    classes={"tw-my-3"}
                                />
                            )}

                            <section aria-labelledby="billing-heading" className="tw-mt-6">
                                <hr />
                                <Checkbox
                                    labelHtml={() => {
                                        return (
                                            <>
                                                He leído y Acepto los{" "}
                                                <a
                                                    href="/terminos-condiciones"
                                                    target="_blank"
                                                    className="text-indigo-600"
                                                >
                                                    Términos y Condiciones
                                                </a>

                                                &nbsp;y{" "}
                                                <a
                                                    href="/politica-reembolsos"
                                                    target="_blank"
                                                    className="text-indigo-600"
                                                >
                                                    Política de Cancelación y Reembolsos
                                                </a>
                                            </>
                                        );
                                    }}
                                    name="terms"
                                    onChange={(e) => setFieldValue("terms", e)}
                                />
                            </section>

                            <div className="tw-pt-6">
                                <Button
                                    label={
                                        isSubmitting
                                            ? "Rentando"
                                            : `Rentar Auto por ${FormatMoney(offer.cost.sale)}`
                                    }
                                    type="submit"
                                    disabled={isSubmitting || !values.terms}
                                    onClick={() => { }}
                                />
                            </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default DataForm;
